import { Controller } from "@hotwired/stimulus"
import { createConsumer } from "@rails/actioncable"

export default class extends Controller {
  static values = { conversationId: String, currentUserId: String }
  static targets = ["messages", "input"]

  connect() {
    this.scrollToBottom()
    this.setupSubscription()
    this.setupMutationObserver()
    this.setupPaginationObserver()
    
    this.visibilityHandler = this.handleVisibilityChange.bind(this)
    document.addEventListener("visibilitychange", this.visibilityHandler)
    
    const textarea = this.element.querySelector('#message-body')
    if (textarea) this.resizeTextarea(textarea)
  }

  disconnect() {
    if (this.channel) this.channel.unsubscribe()
    if (this.mutationObserver) this.mutationObserver.disconnect()
    if (this.paginationObserver) this.paginationObserver.disconnect()
    document.removeEventListener("visibilitychange", this.visibilityHandler)
  }

  handleVisibilityChange() {
    if (document.visibilityState === "visible") {
      const isDisconnected = !this.channel || 
                             this.channel.consumer.connection.disconnected ||
                             !this.channel.consumer.connection.isOpen()

      if (isDisconnected) {
        console.log("Восстановление уснувшего соединения...")
        this.setupSubscription()
        
        const frame = this.element.querySelector("#pagination_frame")
        if (frame) frame.reload()
      }
    }
  }

  setupSubscription() {
    if (this.channel) this.channel.unsubscribe()

    this.channel = createConsumer().subscriptions.create(
      { channel: "ConversationChannel", id: this.conversationIdValue },
      {
        connected: () => {
          console.log(`Connected to Conversation ${this.conversationIdValue}`)
        },
        disconnected: () => {
          console.warn("Connection lost")
        },
        received: (data) => {
          if (data.action === 'messages_read') {
            this.markAllAsRead(data.reader_id);
          }

          if (data.action === "delete") {
            const messageElement = document.getElementById(`message_${data.message_id}`)
            if (messageElement) {
              messageElement.style.transition = 'all 0.3s ease'
              messageElement.style.opacity = '0'
              messageElement.style.transform = 'translateX(20px)'
              
              setTimeout(() => {
                messageElement.remove()
              }, 300)
            }
          } else if (data.action === "update") {
            const existingMessage = document.getElementById(`message_${data.message_id}`)
            if (existingMessage) {
              const template = document.createElement('div')
              template.innerHTML = data.html.trim()
              const newMessageElement = template.firstElementChild
        
              const isMine = String(data.sender_id) === String(this.currentUserIdValue)
              newMessageElement.className = isMine ? 'sc-message-wrapper sc-message-out' : 'sc-message-wrapper sc-message-in'
              newMessageElement.dataset.isMine = isMine.toString()
        
              existingMessage.replaceWith(newMessageElement)
            }
          } else if (data.html) {
            this.insertMessage(data.html, data.sender_id)
          }
        }
      }
    )
  }

  setupPaginationObserver() {
    if (this.paginationObserver) this.paginationObserver.disconnect()

    const frame = this.element.querySelector("#pagination_frame")
    if (!frame) return

    this.paginationObserver = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting) {
        this.loadNextPage(frame)
      }
    }, { 
      root: this.messagesTarget,
      rootMargin: "100px", 
      threshold: 0.1 
    })

    this.paginationObserver.observe(frame)
  }

  loadNextPage(frame) {
    const url = frame.dataset.url
    if (!url || frame.getAttribute("src") || frame.dataset.loading) return

    frame.dataset.loading = "true"
    this.prevScrollHeight = this.messagesTarget.scrollHeight
    frame.src = url
  }

  insertMessage(html, senderId) {
    const template = document.createElement('div')
    template.innerHTML = html.trim()
    const messageElement = template.firstElementChild
    if (!messageElement) return

    const isMine = String(senderId) === String(this.currentUserIdValue)
    messageElement.className = isMine ? 'sc-message-wrapper sc-message-out' : 'sc-message-wrapper sc-message-in'

    this.messagesTarget.appendChild(messageElement)
    this.scrollToBottom()
  }

  setupMutationObserver() {
    this.mutationObserver = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.addedNodes.length > 0) {
          const lastNode = mutation.target.lastElementChild
          const addedNode = mutation.addedNodes[mutation.addedNodes.length - 1]
          
          if (lastNode === addedNode) {
            this.scrollToBottom()
          } 
          
          if (mutation.target.querySelector("#pagination_frame")) {
            if (this.prevScrollHeight) {
              const diff = this.messagesTarget.scrollHeight - this.prevScrollHeight
              this.messagesTarget.scrollTop = diff
              this.prevScrollHeight = null
            }
            this.setupPaginationObserver()
          }
        }
      })
    })
    this.mutationObserver.observe(this.messagesTarget, { childList: true })
  }

  scrollToBottom() {
    requestAnimationFrame(() => {
      this.messagesTarget.scrollTop = this.messagesTarget.scrollHeight
      setTimeout(() => {
        this.messagesTarget.scrollTop = this.messagesTarget.scrollHeight
      }, 100)
    })
  }

  handleInput(event) {
    this.resizeTextarea(event.target)
  }

  resizeTextarea(el) {
    el.style.height = '40px'
    el.style.height = `${Math.min(el.scrollHeight, 150)}px`
  }


  handleKeydown(event) {
    if (event.key === "Enter" && !event.shiftKey) {
      if (event.target.value.trim() === "") return event.preventDefault()
      
      event.preventDefault()
      const form = event.target.closest('form')

      if (form.dataset.editMode === "true") {
        this.sendUpdate(form)
      } else {
        form.requestSubmit()
        event.target.value = ""
        this.resizeTextarea(event.target)
      }
    }
  }

  async sendUpdate(form) {
    const messageId = form.dataset.editMessageId
    const body = form.querySelector('textarea').value
    const url = `/dashboard/messages/${messageId}`

    try {
      const response = await fetch(url, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content,
          'Accept': 'application/json'
        },
        body: JSON.stringify({ body: body })
      })

      if (response.ok) {
        this.clearEditMode(form)
      }
    } catch (error) {
      console.error("Update failed", error)
    }
  }

  clearEditMode(form) {
    form.dataset.editMode = "false"
    form.removeAttribute('data-edit-message-id')
    const textarea = form.querySelector('textarea')
    
    textarea.value = ""
    textarea.style.backgroundColor = ""
    textarea.placeholder = "Write a reply..." 
    
    this.resizeTextarea(textarea)
    textarea.blur()
  }

  clearInput(event) {
    if (event.detail.success) {
      this.clearEditMode(this.element.querySelector('form'))
    }
  }


  showContextMenu(event) {
    const isMine = event.currentTarget.dataset.isMine === "true"
    if (!isMine) return 

    event.preventDefault()

    const menu = document.getElementById('custom-context-menu')
    menu.dataset.activeMessageId = event.currentTarget.dataset.messageId
    
    menu.classList.remove('uk-hidden')

    const menuWidth = menu.offsetWidth
    const menuHeight = menu.offsetHeight
    const windowWidth = window.innerWidth
    const windowHeight = window.innerHeight

    let posX = event.clientX
    let posY = event.clientY


    if (posX + menuWidth > windowWidth) {
      posX = windowWidth - menuWidth - 110
    }

    if (posX < 10) {
      posX = 10
    }

    if (posY + menuHeight > windowHeight) {
      posY = windowHeight - menuHeight - 10 // Сдвигаем вверх
    }

    menu.style.top = `${posY}px`
    menu.style.left = `${posX}px`

    const closeMenu = () => {
      menu.classList.add('uk-hidden')
      document.removeEventListener('click', closeMenu)
      document.removeEventListener('scroll', closeMenu, true)
    }
    
    setTimeout(() => {
      document.addEventListener('click', closeMenu)
      document.addEventListener('scroll', closeMenu, true)
    }, 10)
  }

  editFromMenu(event) {
    event.preventDefault()
    event.stopPropagation()

    const menu = document.getElementById('custom-context-menu')
    const messageId = menu.dataset.activeMessageId
    console.log("Editing message ID:", messageId)

    const messageWrapper = document.getElementById(`message_${messageId}`)
    if (!messageWrapper) {
      console.error("Message wrapper not found for ID:", messageId)
      return
    }

    const textElement = messageWrapper.querySelector('.sc-message-text')
    if (!textElement) {
      console.error("Text element (.sc-message-text) not found")
      return
    }

    const text = textElement.innerText.trim()
    console.log("Text to edit:", text)

    this.fillFormForEdit(messageId, text)
    
    menu.classList.add('uk-hidden')
  }

  copyText(event) {
    event.preventDefault()
    const menu = document.getElementById('custom-context-menu')
    const messageId = menu.dataset.activeMessageId
    const text = document.getElementById(`message_${messageId}`).querySelector('.sc-message-text').innerText
    
    navigator.clipboard.writeText(text).then(() => {
      UIkit.notification({message: 'Text copied!', status: 'success', pos: 'bottom-right', timeout: 1000})
    })
  }

  fillFormForEdit(id, text) {
    const textarea = this.element.querySelector('#message-body')
    const form = textarea.closest('form')
    
    if (!textarea) return

    textarea.value = text
    textarea.scrollTop = 0 
    textarea.focus()
    this.resizeTextarea(textarea)
    
    form.dataset.editMode = "true"
    form.dataset.editMessageId = id
    
    textarea.style.backgroundColor = "#fff9db"
    textarea.placeholder = "Editing message..." 
    
    textarea.scrollIntoView({ behavior: 'smooth', block: 'center' })
  }

  deleteFromMenu(event) {
    event.preventDefault()
    const menu = document.getElementById('custom-context-menu')
    const messageId = menu.dataset.activeMessageId
    this.hideContextMenu()

    const modalElement = document.getElementById('modal-delete-confirm')
    const confirmBtn = modalElement.querySelector('#confirm-delete-button')
    const modal = UIkit.modal(modalElement)

    modal.show()

    confirmBtn.onclick = () => {
      this.performDelete(messageId)
      modal.hide()
    }
  }

  performDelete(messageId) {
    fetch(`/dashboard/messages/${messageId}`, {
      method: 'DELETE',
      headers: {
        'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content,
        'Accept': 'application/json'
      }
    }).then(response => {
      if (response.ok) {
        UIkit.notification({ 
          message: "<span uk-icon='icon: check'></span> Message deleted", 
          status: 'danger', 
          pos: 'bottom-right',
          timeout: 2000
        })
      }
    })
  }

  hideContextMenu() {
    const menu = document.getElementById('custom-context-menu')
    if (menu) {
      menu.classList.add('uk-hidden')
    }
  }

  markAllAsRead(readerId) {
    if (readerId !== this.currentUserIdValue) {
      const icons = this.element.querySelectorAll('.mdi-check');
      icons.forEach(icon => {
        icon.classList.remove('mdi-check');
        icon.classList.add('mdi-check-all', 'uk-text-primary');
        icon.style.opacity = '1';
        icon.style.fontSize = '14px';
      });
    }
  }
}